import type { Money } from "../api/types";

/** Thousands separators on a non-negative integer, without Intl (no float formatting paths). */
function groupThousands(n: number): string {
  const s = String(n);
  let out = "";
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ",";
    out += s[i];
  }
  return out;
}

/**
 * Format integer ngwee as Kwacha, e.g. 125050 -> "K1,250.50". Uses integer division and modulo only;
 * throws on non-integer input, because money must never be a float.
 */
export function formatNgwee(ngwee: number): string {
  if (!Number.isSafeInteger(ngwee)) throw new TypeError(`Money must be integer ngwee, got ${ngwee}`);
  const negative = ngwee < 0;
  const abs = negative ? -ngwee : ngwee;
  const kwacha = (abs - (abs % 100)) / 100;
  const ngweePart = abs % 100;
  return `${negative ? "-" : ""}K${groupThousands(kwacha)}.${String(ngweePart).padStart(2, "0")}`;
}

export function formatMoney(money: Money | null | undefined): string {
  if (!money) return "—";
  if (money.currency !== "ZMW") throw new TypeError(`Unsupported currency ${String(money.currency)}`);
  return formatNgwee(money.amount);
}

/**
 * For rendering: like formatMoney, but bad data (a float, another currency, a missing amount) shows "—"
 * instead of throwing during render. Validation code keeps using the strict formatters.
 */
export function showMoney(money: Money | null | undefined): string {
  try {
    return formatMoney(money);
  } catch {
    return "—";
  }
}

/**
 * Parse a Kwacha amount typed by a person ("1,250.50", "K 300", "12.5") to integer ngwee by string
 * handling only. Returns null for anything that is not a plain non-negative amount with at most 2 decimals.
 */
export function parseKwachaToNgwee(input: string): number | null {
  const typed = input.trim().replace(/^K\s*/i, "");
  // A comma is accepted only as a thousands separator in groups of three ("1,250.50"); "1250,50" or "12,5" (a decimal
  // comma) is refused, never read as a hundred times the amount (review R-W2).
  if (typed.includes(",") && !/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(typed)) return null;
  const cleaned = typed.replace(/,/g, "");
  const m = /^(\d{1,12})(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!m) return null;
  const whole = Number.parseInt(m[1], 10);
  const frac = m[2] ? Number.parseInt(m[2].padEnd(2, "0"), 10) : 0;
  const ngwee = whole * 100 + frac;
  return Number.isSafeInteger(ngwee) ? ngwee : null;
}

/** Integer ngwee to the Kwacha text a person would type ("1250.50"), for prefilling inputs. */
export function ngweeToKwachaInput(ngwee: number | null | undefined): string {
  if (ngwee == null || !Number.isSafeInteger(ngwee) || ngwee < 0) return "";
  return `${(ngwee - (ngwee % 100)) / 100}.${String(ngwee % 100).padStart(2, "0")}`;
}

/** Basis points as a percentage with two decimals, integer arithmetic only: 1000 -> "10.00%". */
export function formatBp(bp: number | null | undefined): string {
  if (bp == null || !Number.isSafeInteger(bp) || bp < 0) return "—";
  return `${(bp - (bp % 100)) / 100}.${String(bp % 100).padStart(2, "0")}%`;
}

/**
 * A percentage typed by a person ("10", "7.5", "12.25") to integer basis points by string handling only; null for
 * anything else.
 */
export function parsePercentToBp(input: string): number | null {
  const m = /^(\d{1,3})(?:\.(\d{1,2}))?\s*%?$/.exec(input.trim());
  if (!m) return null;
  return Number.parseInt(m[1], 10) * 100 + (m[2] ? Number.parseInt(m[2].padEnd(2, "0"), 10) : 0);
}

/** Integer ngwee total of a list of money values (never floats); null when any value is bad. */
export function sumNgwee(values: Array<number | null | undefined>): number | null {
  let total = 0;
  for (const v of values) {
    if (v == null || !Number.isSafeInteger(v)) return null;
    total += v;
  }
  return total;
}

/** Whole-number percentage of part in whole (integer division, floor), or null when whole is zero. */
export function percentOf(part: number, whole: number): number | null {
  if (!Number.isSafeInteger(part) || !Number.isSafeInteger(whole) || whole <= 0) return null;
  return Math.floor((part * 100) / whole);
}
